import { Box, createStyles, FormControlLabel, Grid, IconButton, makeStyles, Switch, Theme } from '@material-ui/core';
import { DeleteOutlineOutlined as DeleteOutlineOutlinedIcon } from '@material-ui/icons';

import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { ChangeEvent, FC, useContext, useEffect, useState } from 'react';

import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import RadioGroup from 'ui-kit/components/radios/RadioGroup';
import { AgentNetDropdownSelector, AgentNetTextInput, CurrencyField } from 'ui-kit/inputs';
import { FieldValidationError } from 'utilities/validation/validation';
import '../../common/styles/fincen-radio-group.scss';
import { Payment } from '../../types';
import { CURRENCY_CODES, HELPER_TEXT, PAYMENT_METHODS, PaymentContext, PayorType } from '../constants';
import { getInitialPayorData } from '../models/payor';
import { PaymentFormProps } from '../types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
    },
    section: {
      marginBottom: theme.spacing(1),
    },

    inputRow: {
      marginBottom: theme.spacing(1),
    },

    radioGroup: {
      flexDirection: 'row',
      marginBottom: theme.spacing(2),
    },

    sectionContainer: {
      marginTop: theme.spacing(1),
    },
  }),
);

const PaymentForm: FC<PaymentFormProps> = ({
  payment,
  index,
  onChange,
  onDelete,
  validationErrors: contextValidationErrors,
  clearValidationErrors,
}) => {
  const classes = useStyles();
  const [paymentContext, setPaymentContext] = useState<PaymentContext>(PaymentContext.STANDARD);

  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { generateButtonClicked, setPaymentValidationErrors } = fincenReportCtx ?? {};

  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);

  const effectiveValidationErrors =
    contextValidationErrors && contextValidationErrors.length > 0 ? contextValidationErrors : validationErrors;

  useEffect(() => {
    let context = PaymentContext.STANDARD;

    if (payment.paymentMethod === 'FOREIGN_CURRENCY') {
      if (payment.currencyCode === 'NOT_LISTED') {
        context = PaymentContext.FOREIGN_CURRENCY_NOT_LISTED;
      } else {
        context = PaymentContext.FOREIGN_CURRENCY;
      }
    } else if (payment.paymentMethod === 'OTHER') {
      context = PaymentContext.OTHER;
    }

    setPaymentContext(context);
  }, [payment.paymentMethod, payment.currencyCode]);

  const buildFieldPath = (fieldName: string, payorIndex?: number, transfereeIndex?: number): string => {
    if (payorIndex !== undefined) {
      return `payments[${index}].payors[${payorIndex}].${fieldName}`;
    } else if (transfereeIndex !== undefined) {
      return `payments[${index}].associatedTransfereeNames[${transfereeIndex}]`;
    } else {
      return `payments[${index}].${fieldName}`;
    }
  };

  const clearFieldError = (fieldName: string, payorIndex?: number, transfereeIndex?: number) => {
    if (!contextValidationErrors || contextValidationErrors.length === 0 || !setPaymentValidationErrors) {
      return;
    }

    const fieldToMatch = buildFieldPath(fieldName, payorIndex, transfereeIndex);
    const filteredErrors = contextValidationErrors.filter((error) => error.field !== fieldToMatch);

    if (filteredErrors.length !== contextValidationErrors.length) {
      setPaymentValidationErrors(filteredErrors);
    }
  };

  const getFieldError = (fieldName: string, payorIndex?: number, transfereeIndex?: number) => {
    // Only show inline validation errors after Generate Report button is clicked
    if (!generateButtonClicked) return undefined;

    const fieldToMatch = buildFieldPath(fieldName, payorIndex, transfereeIndex);
    return effectiveValidationErrors?.find((err) => err.field === fieldToMatch);
  };

  // Helper function to filter alphanumeric characters only
  const handleAlphanumericChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string) => {
    const filteredValue = e.target.value.replace(/[^A-Za-z0-9]/g, '');
    handleFieldChange(fieldName, filteredValue);
  };

  const handleFieldChange = (fieldName: string, value: any, payorIndex?: number, transfereeIndex?: number) => {
    clearFieldError(fieldName, payorIndex, transfereeIndex);

    if (payorIndex !== undefined) {
      const updatedPayors = [...payment.payors];
      updatedPayors[payorIndex] = { ...updatedPayors[payorIndex], [fieldName]: value };
      onChange({ ...payment, payors: updatedPayors });
    } else if (transfereeIndex !== undefined) {
      const updatedNames = [...payment.associatedTransfereeNames];
      updatedNames[transfereeIndex] = value;
      onChange({ ...payment, associatedTransfereeNames: updatedNames });
    } else {
      onChange({ ...payment, [fieldName]: value });
    }
  };

  const handlePaymentMethodChange = (method: string) => {
    let context = PaymentContext.STANDARD;
    const updatedPayment: Partial<Payment> = { paymentMethod: method || '' };

    if (method === 'FOREIGN_CURRENCY') {
      context = PaymentContext.FOREIGN_CURRENCY;
    } else if (method === 'OTHER') {
      context = PaymentContext.OTHER;
      updatedPayment.otherPaymentMethodDescription = '';
    }

    setPaymentContext(context);
    handleFieldChange('paymentMethod', method);
  };

  const handleCurrencyCodeChange = (code: string) => {
    let context = PaymentContext.FOREIGN_CURRENCY;
    const updatedValues: Partial<Payment> = { currencyCode: code || '' };

    if (code === 'NOT_LISTED') {
      context = PaymentContext.FOREIGN_CURRENCY_NOT_LISTED;
      updatedValues.currencyNotListedDescription = '';
    }

    setPaymentContext(context);
    handleFieldChange('currencyCode', code);
  };

  const handlePayorTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const type = event.target.value as PayorType;
    const updatedPayors = payment.payors.map((payor) => ({ ...payor, type }));
    clearFieldError('type', 0);
    onChange({
      ...payment,
      payors: updatedPayors.length ? updatedPayors : [getInitialPayorData(type, 0)],
    });
  };

  const handleAddTransferee = () => {
    clearFieldError('associatedTransfereeNames');
    onChange({
      ...payment,
      associatedTransfereeNames: [...payment.associatedTransfereeNames, ''],
    });
  };

  const handleAddPayor = () => {
    const payorType = payment.payors.length > 0 ? payment.payors[0].type : PayorType.INDIVIDUAL;
    clearFieldError('payors');
    onChange({
      ...payment,
      payors: [...payment.payors, getInitialPayorData(payorType, payment.payors.length)],
    });
  };

  const handleDeletePayor = (idx: number) => {
    const updatedPayors = payment.payors.filter((_, i) => i !== idx);

    if (setPaymentValidationErrors && contextValidationErrors) {
      const filteredErrors = contextValidationErrors
        .filter((error) => {
          const payorIndexMatch = error.field.match(/payments\[(\d+)\]\.payors\[(\d+)\]/);
          if (payorIndexMatch) {
            const errorPaymentIdx = parseInt(payorIndexMatch[1], 10);
            const errorPayorIdx = parseInt(payorIndexMatch[2], 10);
            return !(errorPaymentIdx === index && errorPayorIdx === idx);
          }
          return true;
        })
        .map((error) => {
          const payorIndexMatch = error.field.match(/payments\[(\d+)\]\.payors\[(\d+)\]/);
          if (payorIndexMatch) {
            const errorPaymentIdx = parseInt(payorIndexMatch[1], 10);
            const errorPayorIdx = parseInt(payorIndexMatch[2], 10);
            if (errorPaymentIdx === index && errorPayorIdx > idx) {
              return {
                ...error,
                field: error.field.replace(
                  `payments[${errorPaymentIdx}].payors[${errorPayorIdx}]`,
                  `payments[${errorPaymentIdx}].payors[${errorPayorIdx - 1}]`,
                ),
              };
            }
          }
          return error;
        });
      setPaymentValidationErrors(filteredErrors);
    }

    onChange({
      ...payment,
      payors: updatedPayors,
    });
  };

  const handleDeleteTransferee = (idx: number) => {
    const updatedTransferees = payment.associatedTransfereeNames.filter((_, i) => i !== idx);

    if (setPaymentValidationErrors && contextValidationErrors) {
      const filteredErrors = contextValidationErrors.filter(
        (error) => !error.field.includes(`associatedTransfereeNames[`),
      );
      setPaymentValidationErrors(filteredErrors);
    }
    onChange({
      ...payment,
      associatedTransfereeNames: updatedTransferees,
    });
  };

  const amountError = getFieldError('amount');
  const paymentMethodError = getFieldError('paymentMethod');
  const currencyCodeError = getFieldError('currencyCode');
  const currencyNotListedDescriptionError = getFieldError('currencyNotListedDescription');
  const otherPaymentMethodDescriptionError = getFieldError('otherPaymentMethodDescription');
  const accountNumberError = getFieldError('accountNumber');
  const financialInstitutionNameError = getFieldError('financialInstitutionName');

  const transfereeNameErrors = payment.associatedTransfereeNames.map((_, idx) =>
    getFieldError('associatedTransfereeNames', undefined, idx),
  );

  const payorFirstNameError = getFieldError('firstName', 0);
  const payorLastNameError = getFieldError('lastName', 0);
  const payorLegalNameErrors = payment.payors.map((_, idx) => getFieldError('legalName', idx));
  const payorTypeError = getFieldError('type', 0);

  return (
    <div className={classes.container}>
      <Grid container spacing={3}>
        <Grid item sm={6}>
          <div className={classes.inputRow}>
            <CurrencyField
              label="Payment Amount"
              value={payment.amount}
              onChange={(e) => handleFieldChange('amount', typeof e.target.value === 'number' ? e.target.value : 0)}
              required
              fullWidth
              error={!!amountError}
              helperText={amountError?.message}
              data-testid={`fincen-payment-${index}-amount-input`}
            />
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item sm={6}>
          <div className={classes.inputRow}>
            <AgentNetDropdownSelector
              label="Payment Method"
              name={`payments[${index}].paymentMethod`}
              value={payment.paymentMethod || ''}
              menuOption={(selectedValue: string) => {
                handlePaymentMethodChange(selectedValue);
              }}
              options={PAYMENT_METHODS.map((option) => ({
                name: option.label,
                value: option.value,
              }))}
              required
              showValidation={!!paymentMethodError}
              errs={effectiveValidationErrors}
              fullWidth
              data-testid={`fincen-payment-${index}-method-select`}
            />
          </div>
        </Grid>
        <Grid item sm={6}>
          {(paymentContext === PaymentContext.FOREIGN_CURRENCY ||
            paymentContext === PaymentContext.FOREIGN_CURRENCY_NOT_LISTED) && (
            <div className={classes.inputRow}>
              <AgentNetDropdownSelector
                label="Currency Code"
                name={`payments[${index}].currencyCode`}
                value={payment.currencyCode || ''}
                menuOption={(selectedValue: string) => {
                  handleCurrencyCodeChange(selectedValue);
                }}
                options={CURRENCY_CODES.map((option) => ({
                  name: option.label,
                  value: option.value,
                }))}
                required
                showValidation={!!currencyCodeError}
                errs={effectiveValidationErrors}
                fullWidth
                data-testid={`fincen-payment-${index}-currency-code-select`}
              />
            </div>
          )}

          {paymentContext === PaymentContext.OTHER && (
            <div className={classes.inputRow}>
              <AgentNetTextInput
                label="Other Description"
                value={payment.otherPaymentMethodDescription || ''}
                onChange={(e) => handleFieldChange('otherPaymentMethodDescription', e.target.value)}
                helperText={otherPaymentMethodDescriptionError?.message || ''}
                required
                fullWidth
                error={!!otherPaymentMethodDescriptionError}
                data-testid={`fincen-payment-${index}-other-description-input`}
              />
            </div>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item sm={6}>
          {paymentContext === PaymentContext.FOREIGN_CURRENCY_NOT_LISTED && (
            <div className={classes.inputRow}>
              <AgentNetTextInput
                label="Currency Description"
                value={payment.currencyNotListedDescription || ''}
                onChange={(e) => handleFieldChange('currencyNotListedDescription', e.target.value)}
                helperText={currencyNotListedDescriptionError?.message || HELPER_TEXT.CURRENCY_DESCRIPTION}
                placeholder="e.g. Bitcoin - Digital Currency"
                required
                fullWidth
                error={!!currencyNotListedDescriptionError}
                data-testid={`fincen-payment-${index}-currency-description-input`}
              />
            </div>
          )}
        </Grid>
      </Grid>

      <div className={classes.section}>
        <Grid container spacing={2}>
          <Grid item sm={6}>
            <FormControlLabel
              className={classes.inputRow}
              control={
                <Switch
                  size="small"
                  checked={!payment.fromFinancialInstitution}
                  onChange={(_, checked) => handleFieldChange('fromFinancialInstitution', !checked)}
                  color="primary"
                  data-testid={`fincen-payment-${index}-not-from-financial-institution-switch`}
                />
              }
              label="Payment not from financial institution account"
            />
          </Grid>
        </Grid>

        {payment.fromFinancialInstitution && (
          <Grid container spacing={2}>
            <Grid item sm={6}>
              <AgentNetTextInput
                label="Account No."
                value={payment.accountNumber || ''}
                onChange={(e) => handleAlphanumericChange(e, 'accountNumber')}
                required
                fullWidth
                error={!!accountNumberError}
                helperText={accountNumberError?.message}
                data-testid={`fincen-payment-${index}-account-no-input`}
              />
            </Grid>
            <Grid item sm={6}>
              <AgentNetTextInput
                label="Financial Institution Legal Name"
                value={payment.financialInstitutionName || ''}
                onChange={(e) => handleFieldChange('financialInstitutionName', e.target.value)}
                required
                fullWidth
                error={!!financialInstitutionNameError}
                helperText={financialInstitutionNameError?.message}
                data-testid={`fincen-payment-${index}-financial-institution-legal-name-input`}
              />
            </Grid>
          </Grid>
        )}
      </div>

      <div className={classes.section}>
        <Grid container spacing={2}>
          <Grid item sm={6}>
            <FormControlLabel
              className={classes.inputRow}
              control={
                <Switch
                  size="small"
                  checked={payment.associatedWithAllTransferees}
                  onChange={(_, checked) => {
                    if (!checked && payment.associatedTransfereeNames.length === 0) {
                      onChange({
                        ...payment,
                        associatedWithAllTransferees: checked,
                        associatedTransfereeNames: [''],
                      });
                    } else {
                      handleFieldChange('associatedWithAllTransferees', checked);
                    }
                  }}
                  color="primary"
                  data-testid={`fincen-payment-${index}-associated-with-all-transferees-switch`}
                />
              }
              label="Payment associated with all recorded transferees"
            />
          </Grid>
        </Grid>

        {!payment.associatedWithAllTransferees && (
          <>
            {payment.associatedTransfereeNames.map((name, idx) => (
              <Grid container spacing={2} key={`transferee-container-${idx}`}>
                <Grid item sm={6}>
                  <div className={classes.inputRow}>
                    {idx === 0 ? (
                      <AgentNetTextInput
                        label="Associated Transferee Legal Name"
                        value={name}
                        onChange={(e) => handleFieldChange('associatedTransfereeNames', e.target.value, undefined, idx)}
                        required
                        fullWidth
                        error={!!transfereeNameErrors[idx]}
                        helperText={transfereeNameErrors[idx]?.message}
                        data-testid={`fincen-payment-${index}-associated-transferee-${idx}-legal-name-input`}
                      />
                    ) : (
                      <Box display="flex" alignItems="center">
                        <Box flexGrow={1} mr={1}>
                          <AgentNetTextInput
                            label="Associated Transferee Legal Name"
                            value={name}
                            onChange={(e) =>
                              handleFieldChange('associatedTransfereeNames', e.target.value, undefined, idx)
                            }
                            required
                            fullWidth
                            error={!!transfereeNameErrors[idx]}
                            helperText={transfereeNameErrors[idx]?.message}
                            data-testid={`fincen-payment-${index}-associated-transferee-${idx}-legal-name-input`}
                          />
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTransferee(idx);
                          }}
                          aria-label="delete transferee"
                          data-testid={`fincen-payment-${index}-transferee-${idx}-delete-button`}
                          size="small"
                        >
                          <DeleteOutlineOutlinedIcon color="secondary" />
                        </IconButton>
                      </Box>
                    )}
                  </div>
                </Grid>
              </Grid>
            ))}

            <Grid item xs={12}>
              <AgentNetButton
                variant="outlined"
                color="primary"
                plusIcon
                size="small"
                onClick={handleAddTransferee}
                data-testid={`fincen-payment-${index}-add-associated-transferee-button`}
              >
                Add Associated Transferee Legal Name
              </AgentNetButton>
            </Grid>
          </>
        )}
      </div>

      <div className={classes.section}>
        <Grid container spacing={2}>
          <Grid item sm={6}>
            <FormControlLabel
              className={classes.inputRow}
              control={
                <Switch
                  size="small"
                  checked={payment.allPayorsAreTransferees}
                  onChange={(_, checked) => handleFieldChange('allPayorsAreTransferees', checked)}
                  color="primary"
                  data-testid={`fincen-payment-${index}-all-payors-are-transferees-switch`}
                />
              }
              label="All payors are recorded transferees"
            />
          </Grid>
        </Grid>

        {!payment.allPayorsAreTransferees && (
          <div className={`fincen-radio-group`}>
            <Grid item xs={12}>
              <RadioGroup
                label="Payor Type"
                options={[
                  {
                    label: 'Individual',
                    value: PayorType.INDIVIDUAL,
                    dataTestId: `fincen-payment-${index}-payor-type-individual-radio`,
                  },
                  {
                    label: 'Entity',
                    value: PayorType.ENTITY,
                    dataTestId: `fincen-payment-${index}-payor-type-entity-radio`,
                  },
                ]}
                onChange={handlePayorTypeChange}
                selected={{ value: payment.payors.length > 0 ? payment.payors[0].type : PayorType.INDIVIDUAL }}
                row={true}
                compact={true}
                border={true}
                name={`payments[${index}].payors[0].type`}
                required
                showValidation={!!payorTypeError}
                errs={effectiveValidationErrors}
                data-testid={`fincen-payment-${index}-payor-type-radio`}
              />
            </Grid>

            <div className={classes.sectionContainer}>
              {payment.payors.length > 0 &&
                (payment.payors[0].type === PayorType.INDIVIDUAL ? (
                  <Grid container spacing={3} className={classes.sectionContainer}>
                    <Grid item xs={12} sm={3}>
                      <AgentNetTextInput
                        label="First Name"
                        value={payment.payors[0].firstName || ''}
                        onChange={(e) => handleFieldChange('firstName', e.target.value, 0)}
                        required
                        fullWidth
                        error={!!payorFirstNameError}
                        helperText={payorFirstNameError?.message}
                        data-testid={`fincen-payment-${index}-payor-firstname-input`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <AgentNetTextInput
                        label="Middle Name"
                        value={payment.payors[0].middleName || ''}
                        onChange={(e) => handleFieldChange('middleName', e.target.value, 0)}
                        fullWidth
                        data-testid={`fincen-payment-${index}-payor-middlename-input`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <AgentNetTextInput
                        label="Last Name"
                        value={payment.payors[0].lastName || ''}
                        onChange={(e) => handleFieldChange('lastName', e.target.value, 0)}
                        required
                        fullWidth
                        error={!!payorLastNameError}
                        helperText={payorLastNameError?.message}
                        data-testid={`fincen-payment-${index}-payor-lastname-input`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <AgentNetTextInput
                        label="Suffix"
                        value={payment.payors[0].suffix || ''}
                        onChange={(e) => handleFieldChange('suffix', e.target.value, 0)}
                        fullWidth
                        data-testid={`fincen-payment-${index}-payor-suffix-input`}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <>
                    {payment.payors.map((payor, idx) => (
                      <Grid item sm={6} key={`payor-${index}-${idx}`}>
                        <div className={classes.inputRow}>
                          {idx === 0 ? (
                            <Grid container spacing={3} className={classes.sectionContainer}>
                              <Grid item xs={12} sm={12}>
                                <AgentNetTextInput
                                  label={`Payor Legal Name`}
                                  value={payor.legalName || ''}
                                  onChange={(e) => handleFieldChange('legalName', e.target.value, idx)}
                                  required
                                  fullWidth
                                  error={!!payorLegalNameErrors[idx]}
                                  helperText={payorLegalNameErrors[idx]?.message}
                                  data-testid={`fincen-payment-${index}-payor-${idx}-legal-name-input`}
                                />
                              </Grid>
                            </Grid>
                          ) : (
                            <Box display="flex" alignItems="center">
                              <Box flexGrow={1} mr={1}>
                                <AgentNetTextInput
                                  label={`Payor Legal Name`}
                                  value={payor.legalName || ''}
                                  onChange={(e) => handleFieldChange('legalName', e.target.value, idx)}
                                  required
                                  fullWidth
                                  error={!!payorLegalNameErrors[idx]}
                                  helperText={payorLegalNameErrors[idx]?.message}
                                  data-testid={`fincen-payment-${index}-payor-${idx}-legal-name-input`}
                                />
                              </Box>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePayor(idx);
                                }}
                                aria-label="delete payor"
                                data-testid={`fincen-payment-${index}-payor-${idx}-delete-button`}
                                size="small"
                              >
                                <DeleteOutlineOutlinedIcon color="secondary" />
                              </IconButton>
                            </Box>
                          )}
                        </div>
                      </Grid>
                    ))}
                  </>
                ))}

              {payment.payors.length > 0 && payment.payors[0].type === PayorType.ENTITY && (
                <Grid item xs={12} style={{ marginTop: 8 }}>
                  <AgentNetButton
                    variant="outlined"
                    color="primary"
                    plusIcon
                    size="small"
                    onClick={handleAddPayor}
                    data-testid={`fincen-payment-${index}-add-payor-button`}
                  >
                    Payor
                  </AgentNetButton>
                </Grid>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentForm;
